import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { ShieldCheck, ArrowRight, Car } from "lucide-react";

const MODELS = [
  "Audi RS3 Limousine",
  "Mercedes-AMG G 63",
  "Audi RS6",
  "MINI Cooper S Cabrio",
  "Mercedes GLC 220 d Coupé",
];

const FEATURES = [
  { t: "Ab 1 Tag mieten", d: "Kurzfristig verfügbar – ab einem Tag bis mehreren Wochen." },
  { t: "Vollversicherung", d: "Umfassender Versicherungsschutz inklusive – ohne versteckte Kosten." },
  { t: "Kilometer-Paket", d: "Großzügige Freikilometer, individuell erweiterbar." },
  { t: "Anlieferung", d: "Auf Wunsch bringen wir das Fahrzeug direkt zu Ihnen." },
];

export default function Mietwagen() {
  useEffect(() => window.scrollTo(0, 0), []);
  const [offers, setOffers] = useState(null);
  useEffect(() => {
    api.get("/offers", { params: { section: "mietwagen" } })
      .then(({ data }) => setOffers(data))
      .catch(() => setOffers([]));
  }, []);
  // Admin-gepflegte Angebote; Fallback: statische Basisliste
  const models = offers && offers.length
    ? offers.map((o) => ({ title: o.title, note: o.note }))
    : MODELS.map((m) => ({ title: m, note: "" }));
  useSeo({
    title: "Mietwagen Köln · Premium Mietfahrzeuge | DNA Automotive",
    description: "Premium-Mietwagen in Köln: Audi RS3, Mercedes-AMG G 63, Audi RS6, MINI Cooper S Cabrio, Mercedes GLC Coupé u. v. m. auf Anfrage. Jetzt Mietwagen anfragen bei DNA Automotive.",
    path: "/mietwagen",
    breadcrumbs: [{ name: "Mietwagen", url: "/mietwagen" }],
  });
  const scrollContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-obsidian min-h-screen" data-testid="mietwagen-page">
      <Header />

      {/* Hero – bewusst clean, ohne Fahrzeugbilder */}
      <section className="relative pt-40 pb-28 px-6 md:px-10 overflow-hidden grain">
        <div className="absolute inset-0 z-0 bg-obsidian-surface" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/50" />
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">Premium · Mietwagen</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            Fahren Sie<br /><span className="text-brand-bright">Ihren Traum.</span>
          </h1>
          <p className="text-zinc-300 max-w-2xl text-lg mt-8">
            Premium-Mietwagen für einen Tag, ein Wochenende oder länger – unser Fuhrpark wartet in der MotorWorld Köln auf Sie.
          </p>
          <button
            onClick={scrollContact}
            className="group mt-10 inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover transition-colors"
            data-testid="rental-hero-cta"
          >
            Mietwagen anfragen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-10 pb-16" data-testid="rental-features">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {FEATURES.map((f) => (
            <div key={f.t} className="bg-obsidian p-6 md:p-8">
              <ShieldCheck className="w-6 h-6 text-brand-bright mb-4" />
              <h3 className="font-heading font-extrabold uppercase text-white text-base md:text-lg mb-2">{f.t}</h3>
              <p className="text-zinc-400 text-sm">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Aktuell angebotene Mietfahrzeuge – clean, ohne Bilder/Preise */}
      <section className="px-6 md:px-10 py-16" data-testid="rental-models">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-3">Unsere Mietfahrzeuge</h2>
          <p className="text-zinc-500 max-w-2xl mb-12 text-sm">Aktuell angebotene Modelle – Verfügbarkeit und Konditionen auf Anfrage.</p>

          <div className="border-t border-white/10">
            {models.map((m, i) => (
              <motion.button
                key={m.title}
                onClick={scrollContact}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative flex w-full items-center justify-between border-b border-white/10 py-7 md:py-9 text-left hover:bg-white/[0.02] transition-colors duration-500"
                data-testid={`rental-model-${i}`}
              >
                <div className="flex items-center gap-5 md:gap-8 px-2 md:px-4">
                  <span className="font-heading font-black text-3xl md:text-4xl leading-none text-zinc-800 group-hover:text-brand-bright transition-colors duration-500 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <span className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl md:text-4xl group-hover:text-brand-bright transition-colors duration-500 block">
                      {m.title}
                    </span>
                    {m.note && <span className="text-zinc-500 text-xs uppercase tracking-widest mt-1 block">{m.note}</span>}
                  </span>
                </div>
                <Car className="w-6 h-6 text-zinc-700 group-hover:text-brand-bright transition-colors duration-500 mx-2 md:mx-4 shrink-0" />
                <div className="absolute bottom-0 left-0 h-px w-0 bg-brand-light group-hover:w-full transition-all duration-700" />
              </motion.button>
            ))}
          </div>

          <p className="text-zinc-400 text-sm md:text-base mt-10 italic" data-testid="rental-more-models">
            Weitere Modelle auf Anfrage verfügbar.
          </p>

          <button
            onClick={scrollContact}
            className="group mt-10 inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover transition-colors"
            data-testid="rental-models-cta"
          >
            Mietwagen anfragen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <ContactSection prefillVehicle="Mietwagen-Anfrage" />
      <Footer />
      <WhatsAppButton prefill="Hallo DNA Automotive, ich interessiere mich für einen Mietwagen." />
    </div>
  );
}
